import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { cloudflareStreamService } from '../services/cloudflareStream';

const StreamContext = createContext();

const initialState = {
  streams: [],
  activeStreams: [],
  viewers: {},
  hosts: [],
  restreamTargets: [],
  isLoading: false,
  error: null,
  user: {
    role: 'viewer', // 'host', 'viewer', 'admin'
    id: null,
    name: '',
  },
  settings: {
    apiKey: '',
    accountId: '',
    defaultQuality: '720p',
    enableChat: true,
    enableRecording: true,
  }
};

function streamReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    
    case 'SET_STREAMS':
      return { ...state, streams: action.payload };
    
    case 'ADD_STREAM':
      return { 
        ...state, 
        streams: [...state.streams, action.payload],
        activeStreams: action.payload.status === 'live' 
          ? [...state.activeStreams, action.payload]
          : state.activeStreams
      };
    
    case 'UPDATE_STREAM':
      const updatedStreams = state.streams.map(stream =>
        stream.id === action.payload.id ? { ...stream, ...action.payload } : stream
      );
      const updatedActiveStreams = updatedStreams.filter(stream => stream.status === 'live');
      return {
        ...state,
        streams: updatedStreams,
        activeStreams: updatedActiveStreams
      };
    
    case 'REMOVE_STREAM':
      return {
        ...state,
        streams: state.streams.filter(stream => stream.id !== action.payload),
        activeStreams: state.activeStreams.filter(stream => stream.id !== action.payload)
      };
    
    case 'SET_VIEWERS':
      return { ...state, viewers: { ...state.viewers, ...action.payload } };
    
    case 'ADD_HOST':
      return { ...state, hosts: [...state.hosts, action.payload] };
    
    case 'REMOVE_HOST':
      return { 
        ...state, 
        hosts: state.hosts.filter(host => host.id !== action.payload) 
      };
    
    case 'SET_RESTREAM_TARGETS':
      return { ...state, restreamTargets: action.payload };
    
    case 'ADD_RESTREAM_TARGET':
      return { 
        ...state, 
        restreamTargets: [...state.restreamTargets, action.payload] 
      };
    
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload } };
    
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    
    default:
      return state;
  }
}

export function StreamProvider({ children }) {
  const [state, dispatch] = useReducer(streamReducer, initialState);

  // Initialize and fetch streams
  useEffect(() => {
    fetchStreams();
  }, []);

  const fetchStreams = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const streams = await cloudflareStreamService.getStreams();
      dispatch({ type: 'SET_STREAMS', payload: streams });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const createStream = async (streamData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const newStream = await cloudflareStreamService.createStream(streamData);
      dispatch({ type: 'ADD_STREAM', payload: newStream });
      return newStream;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const startStream = async (streamId) => {
    try {
      const stream = await cloudflareStreamService.startStream(streamId);
      dispatch({ type: 'UPDATE_STREAM', payload: stream });
      return stream;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const stopStream = async (streamId) => {
    try {
      const stream = await cloudflareStreamService.stopStream(streamId);
      dispatch({ type: 'UPDATE_STREAM', payload: stream });
      return stream;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const deleteStream = async (streamId) => {
    try {
      await cloudflareStreamService.deleteStream(streamId);
      dispatch({ type: 'REMOVE_STREAM', payload: streamId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const addRestreamTarget = async (streamId, target) => {
    try {
      const restreamTarget = await cloudflareStreamService.addRestreamTarget(streamId, target);
      dispatch({ type: 'ADD_RESTREAM_TARGET', payload: restreamTarget });
      return restreamTarget;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const value = {
    ...state,
    dispatch,
    fetchStreams,
    createStream,
    startStream,
    stopStream,
    deleteStream,
    addRestreamTarget,
  };

  return (
    <StreamContext.Provider value={value}>
      {children}
    </StreamContext.Provider>
  );
}

export function useStream() {
  const context = useContext(StreamContext);
  if (!context) {
    throw new Error('useStream must be used within a StreamProvider');
  }
  return context;
} 